/**
 * お問い合わせ API。
 *   POST /inquiries                     … 内容を保存して番号（No.0012）を付け、開発者にメールで知らせる
 *   GET  /inquiries                     … 自分のお問い合わせの一覧（新しい順）と、返信の未読の数
 *   GET  /inquiries/{id}                … 自分のお問い合わせのやり取り（古い順）。開くと、返信を読んだ印が付く
 *   POST /inquiries/{id}/messages       … 自分のお問い合わせに追記する（開発者にメールが届く）
 * 失敗したときは、サーバーが返した日本語のメッセージ（detail）をそのまま Error に入れる。404 は err.status で見分ける。
 */
import { apiFetch } from './authApi';
import { getApiBaseUrl } from './apiBase';

const API_BASE_URL = getApiBaseUrl();
const FALLBACK = '送信に失敗しました。時間をおいて、もう一度お試しください';

async function request(path, options, fallback = FALLBACK) {
  const response = await apiFetch(`${API_BASE_URL}${path}`, options);
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    const error = new Error(typeof err.detail === 'string' ? err.detail : fallback);
    error.status = response.status;
    throw error;
  }
  return response.json();
}

const postJson = (body) => ({
  method: 'POST',
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify(body),
});

/**
 * お問い合わせを送る。
 * @param {{category: string, body: string}} input
 * @returns {Promise<{id: string, number: number, numberLabel: string, status: string}>}
 */
export function createInquiry({ category, body }) {
  return request('/inquiries', postJson({ category, body }));
}

/** 自分のお問い合わせの一覧。@returns {Promise<{inquiries: object[], unreadCount: number}>} */
export function listInquiries() {
  return request('/inquiries', undefined, '一覧を読み込めませんでした');
}

/** やり取りの全部。@returns {Promise<object>} 見つからなければ、status 404 の Error。 */
export function getInquiry(id) {
  return request(`/inquiries/${encodeURIComponent(id)}`, undefined, '読み込めませんでした');
}

/** 追記する。@returns {Promise<{id: string, from: string, body: string, createdAt: string}>} */
export function sendInquiryMessage(id, body) {
  return request(`/inquiries/${encodeURIComponent(id)}/messages`, postJson({ body }));
}

// ---- 開発者（role=dev）用。バックエンドが、dev 以外を 403 で断る ----

/** 全員分の一覧。filter = 'needs_reply'（返信待ちだけ）| 'all'。@returns {Promise<{inquiries: object[], needsReplyCount: number}>} */
export function listDevInquiries(filter = 'all') {
  return request(`/dev/inquiries?status=${encodeURIComponent(filter)}`, undefined, '一覧を読み込めませんでした');
}

/** 1 件のやり取り + 送った人・端末。本人の「読んだ印」は付かない。 */
export function getDevInquiry(id) {
  return request(`/dev/inquiries/${encodeURIComponent(id)}`, undefined, '読み込めませんでした');
}

/** 開発者が返信する（送った本人にプッシュ通知が届く）。 */
export function sendDevReply(id, body) {
  return request(`/dev/inquiries/${encodeURIComponent(id)}/messages`, postJson({ body }));
}

/** 対応済み（'closed'）/ 未対応（'open'）を切り替える。 */
export function setInquiryStatus(id, status) {
  return request(`/dev/inquiries/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ status }),
  });
}
